import React, { useMemo } from "react";
import type { OptimizedBrick } from "../../utils/brickOptimizer";
import { LEGO_COLORS_3D } from "./constants";
import {
  brickDepth,
  makeCamera,
  projectPoint,
  renderBrick2D,
  renderBrick3D,
  type BrickRendering,
  type Rotation,
} from "./renderers";
import "./BrickShapeRenderer.css";

interface BrickShapeRendererProps {
  bricks: OptimizedBrick[];
  canvasSize: { width: number; height: number };
  pixelSize: number;
  is3D: boolean;
  rotation: Rotation;
  scale: number;
}

const BrickShapeRenderer: React.FC<BrickShapeRendererProps> = ({
  bricks,
  canvasSize,
  pixelSize,
  is3D,
  rotation,
  scale,
}) => {
  const brickRenderings = useMemo(() => {
    const renderings: BrickRendering[] = [];

    bricks.forEach((brick) => {
      const baseColor = LEGO_COLORS_3D[brick.colorIndex] || "#CCCCCC";
      const width = brick.brickType.width * pixelSize;
      const height = brick.brickType.height * pixelSize;

      renderings.push({
        type: is3D ? "isometric" : "topdown",
        x: brick.x,
        y: brick.y,
        z: brick.z ?? 0,
        width,
        height,
        color: baseColor,
        colorIndex: brick.colorIndex,
        colorName: brick.colorName,
        brickTypeName: brick.brickType.name,
        rotation: brick.rotation || 0,
      });
    });

    return renderings;
  }, [bricks, pixelSize, is3D]);

  // Auto-center (isometric): mid of the bricks' projected bounds, so the
  // model lands on the canvas instead of drawing away from the world origin.
  // Bounds (not the mean) — a solid model has most of its bricks in the low
  // layers, so a mean would bias the render downward.
  const isoOffset = useMemo(() => {
    if (!is3D || brickRenderings.length === 0) return { x: 0, y: 0 };
    const cam = makeCamera(rotation);
    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;
    brickRenderings.forEach((r) => {
      const wx = r.x * pixelSize + r.width / 2;
      const wy = r.y * pixelSize + r.height / 2;
      const p = projectPoint(cam, wx, wy, (r.z ?? 0) * pixelSize);
      minX = Math.min(minX, p.x);
      maxX = Math.max(maxX, p.x);
      minY = Math.min(minY, p.y);
      maxY = Math.max(maxY, p.y);
    });
    const mid = { x: (minX + maxX) / 2, y: (minY + maxY) / 2 };
    // The offset shifts the projected mid onto the canvas center
    return { x: mid.x, y: mid.y };
  }, [brickRenderings, is3D, rotation, pixelSize]);

  const renderCanvas = useMemo(() => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;

    canvas.width = canvasSize.width;
    canvas.height = canvasSize.height;

    // Background (warm subtle, per .impeccable.md)
    ctx.fillStyle = "#f8f5f0";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;

    // Sort bricks for proper rendering order — in 3D the camera orbits the
    // model, so the draw order follows the camera depth (far faces first)
    const sortedBricks = [...brickRenderings].sort((a, b) => {
      if (is3D) {
        const cam = makeCamera(rotation);
        return brickDepth(cam, a, pixelSize) - brickDepth(cam, b, pixelSize);
      }
      return 0;
    });

    // Apply zoom around canvas center
    if (scale !== 1) {
      ctx.save();
      ctx.translate(centerX, centerY);
      ctx.scale(scale, scale);
      ctx.translate(-centerX, -centerY);
    }

    // Render all bricks
    sortedBricks.forEach((brick) => {
      if (brick.type === "topdown") {
        renderBrick2D(brick, ctx, pixelSize);
      } else {
        renderBrick3D(brick, ctx, centerX, centerY, pixelSize, rotation, isoOffset);
      }
    });

    if (scale !== 1) {
      ctx.restore();
    }

    return canvas;
  }, [brickRenderings, is3D, rotation, canvasSize, scale, pixelSize, isoOffset]);

  if (!renderCanvas) return null;

  return (
    <div className="brick-shape-renderer">
      <canvas
        className="brick-canvas"
        width={canvasSize.width}
        height={canvasSize.height}
        aria-hidden="true"
        ref={(canvas) => {
          if (canvas && renderCanvas) {
            const ctx = canvas.getContext("2d");
            if (ctx) {
              ctx.clearRect(0, 0, canvas.width, canvas.height);
              ctx.drawImage(renderCanvas, 0, 0);
            }
          }
        }}
      />
    </div>
  );
};

export default React.memo(BrickShapeRenderer);
